'use client';

import { cn, tv } from '../_lib/utils';
import type { ReactNode } from 'react';
import { isValidRichText } from '../_lib/isValidRichText';
import { Anchor } from '../Anchor';
import ArrowUpRightIcon from '../_assets/icons/arrowUpRight.svg';
import { Text } from '../Text';

export type CrossLinksProps = {
	className?: string;
	label?: string;
	cards?: CrossLinkItemProps[];
	button?: ReactNode;
	onCardClick?(id: string): void;
};

export function CrossLinks(props: CrossLinksProps) {
	return (
		<article className={cn('container-xl py-5 md:py-10', props.className)}>
			{props.label && (
				<Text className='text-text-label mb-6' styleType='label-lg'>
					{props.label}
				</Text>
			)}
			{props.cards && (
				<div className='grid gap-4 grid-cols-1 md:grid-cols-2 md:gap-6 @-xs:grid-cols-1! @-xl:grid-cols-2!'>
					{props.cards.map((card, index) => (
						<CrossLinkItem {...card} key={index} onCardClick={props.onCardClick} />
					))}
				</div>
			)}
			{props.button && <div className='w-full mt-6 text-center'>{props.button}</div>}
		</article>
	);
}

const styles = tv({
	slots: {
		base: 'group relative transition-all duration-normal ease-smooth p-5 md:p-8 md:pr-6 rounded-[0.75rem] md:rounded-[1.25rem] bg-accordion-item-background/10 hover:bg-accordion-item-hover-background/20 hover:-translate-y-1',
		gradientBasis:
			'gradient-border before:transition-opacity before:duration-normal before:ease-smooth before:rounded-[0.75rem] md:before:rounded-[1.25rem] before:border before:border-transparent ',
		gradientBorder: 'before:[background:var(--gradient-cross-links-item-border)_border-box] before:opacity-100 hover:before:opacity-0',
		gradientBorderHover:
			'before:[background:var(--gradient-cross-links-item-hover-border)_border-box] before:opacity-0 group-hover:before:opacity-100',
	},
});

export type CrossLinkItemProps = {
	copy?: ReactNode;
	className?: string;
	label?: string;
	href?: string;
	title?: ReactNode;
	id?: string;
	onCardClick?(id: string): void;
	learnMore?: {
		learn: string;
		more: string;
	};
};

function CrossLinkItem(props: CrossLinkItemProps) {
	const { base, gradientBasis: gradient, gradientBorder: darkGradient, gradientBorderHover: darkGradientHover } = styles();

	return (
		<article className={`${base()} ${gradient()} ${darkGradient()} ${props.className || ''}`}>
			<div className={`flex flex-col gap-2 h-full md:flex-row md:gap-4`}>
				<div className={`absolute left-0 top-0 w-full h-full ${gradient()} ${darkGradientHover()}`} />
				<div className='flex flex-col grow'>
					{props.label && (
						<Text className='block mb-4' styleType='label-sm'>
							{props.label}
						</Text>
					)}
					<Anchor
						className={`before:bottom-0 before:left-0 before:right-0 before:top-0 before:absolute before:content[""] before:z-10`}
						href={props.href}
					>
						{props.title && (
							<Text as='h2' styleType='heading-3xs'>
								{props.title}
							</Text>
						)}
					</Anchor>
					{isValidRichText(props.copy) && (
						<Text className={'pt-2 opacity-80'} styleType='body-md'>
							{props.copy}
						</Text>
					)}
				</div>
				<div className='self-end'>
					<ArrowUpRightIcon
						className={
							'h-6 w-6 md:translate-y-2 md:-translate-x-2 md:opacity-0 md:transition-[opacity_transform] md:duration-normal md:ease-smooth md:group-hover:opacity-100 md:group-hover:translate-y-0 md:group-hover:translate-x-0'
						}
					/>
				</div>
			</div>
		</article>
	);
}
